import React, { Fragment, useEffect, useState } from "react";
import { Button, DatePicker, Pagination, Select } from "antd";
import Layout from "../core/Layout";
import {
    listExpenses,
    deleteExpenses,
    updateExpense,
} from "../actions/expensesActions";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Bar, Line } from "react-chartjs-2";
import { listDoctors } from "../actions/doctorActions"; // Import doctor list action
import { listUsers, deleteUser } from "../actions/userActions";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isoWeek from "dayjs/plugin/isoWeek";

// Extend dayjs with required plugins
dayjs.extend(isSameOrBefore);
dayjs.extend(isoWeek);

const { RangePicker } = DatePicker;
const { Option } = Select;

const ListExpenses = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [view, setView] = useState("daily");
    const [filters, setFilters] = useState({
        startDate: dayjs().subtract(6, "days").format("YYYY-MM-DD"),
        endDate: dayjs().add(1, 'days').format("YYYY-MM-DD"),
        doctor: null,
    });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        pageSize: 10,
    });
	console.log("------------------------------------------------------");
	console.log('Current View:', view);
	console.log('Dayjs Start Date:', dayjs());
	console.log('Filters:', filters);
	console.log('Start Date:', filters.startDate);
	console.log('End Date:', filters.endDate);
	console.log("------------------------------------------------------");

    const expenseList = useSelector((state) => state.expenseList);
    const {
        loading,
        error,
        expenses: { expense, totalExpenses, expensesByDoctor } = {},
    } = expenseList;

    const userList = useSelector((state) => state.userList);
    const { users } = userList;

    const userLogin = useSelector((state) => state.userLogin);
    const { userInfo } = userLogin;

    const expensesDelete = useSelector((state) => state.expensesDelete);
    const { success: successDelete } = expensesDelete;

    useEffect(() => {
        if (userInfo) {
            dispatch(listUsers());
            const { startDate, endDate, doctor } = filters;
            dispatch(
                listExpenses(
                    pagination.currentPage,
                    pagination.pageSize,
                    doctor,
                    startDate,
                    endDate
                )
            );
        } else {
            navigate("/signin");
        }
    }, [dispatch, successDelete, userInfo, filters, pagination]);

    useEffect(() => {
        if (userInfo) {
            dispatch(listDoctors()); // Fetch list of doctors when the component mounts
        }
    }, [dispatch, userInfo]);

    const deleteHandler = (id) => {
        if (window.confirm("Are you sure?")) {
            dispatch(deleteExpenses(id));
        }
    };

    const handleDateChange = (dates) => {
        if (dates) {
            setFilters((prev) => ({
                ...prev,
                startDate: dates[0].format("YYYY-MM-DD"),
                endDate: dates[1].add(1, 'day').format("YYYY-MM-DD"),
            }));
        } else {
            setFilters((prev) => ({ ...prev, startDate: null, endDate: null }));
        }
    };

    const handleDoctorChange = (value) => {
        setFilters((prev) => ({ ...prev, doctor: value }));
    };

    const applyFilters = () => {
        const { startDate, endDate, doctor } = filters;

        const formattedStartDate = startDate
            ? dayjs(startDate).format("YYYY-MM-DD")
            : null;
        const formattedEndDate = endDate
            ? dayjs(endDate).format("YYYY-MM-DD")
            : null;
        dispatch(
            listExpenses(
                1,
                pagination.pageSize,
                doctor,
                formattedStartDate,
                formattedEndDate
            )
        );
        setPagination({ currentPage: 1, pageSize: pagination.pageSize });
    };

    const handlePaginationChange = (page, pageSize) => {
        setPagination({ currentPage: page, pageSize });
        const { startDate, endDate, doctor } = filters;
        const formattedStartDate = startDate
            ? dayjs(startDate).format("YYYY-MM-DD")
            : null;
        const formattedEndDate = endDate
            ? dayjs(endDate).format("YYYY-MM-DD")
            : null;
        dispatch(
            listExpenses(
                page,
                pageSize,
                doctor,
                formattedStartDate,
                formattedEndDate
            )
        );
    };
// Replace your existing function with this
const processExpenseData = (timeframe, startDate = null, endDate = null) => {
  const today = dayjs().startOf("day");
  let labels = [];
  const earningsByPeriod = {};

  // --- Build labels (oldest -> newest) and init buckets ---
  if (timeframe === "custom" && startDate && endDate) {
    const start = dayjs(startDate).startOf("day");
    const end = dayjs(endDate).endOf("day");
    let current = start.clone();
    while (current.isSameOrBefore(end, "day")) {
      const key = current.format("YYYY-MM-DD"); // canonical key for custom/daily
      labels.push(key);
      earningsByPeriod[key] = 0;
      current = current.add(1, "day");
    }
  } else if (timeframe === "daily") {
    // last 7 days, oldest -> newest
    for (let i = 6; i >= 0; i--) {
      const d = dayjs(today).subtract(i, "day");
      const key = d.format("YYYY-MM-DD");
      labels.push(key);
      earningsByPeriod[key] = 0;
    }
  } else if (timeframe === "weekly") {
    // last 4 weeks, oldest -> newest, label: "DD MMM - DD MMM YYYY"
    const weeks = 4;
    for (let i = weeks - 1; i >= 0; i--) {
      const weekStart = dayjs(today).subtract(i, "week").startOf("isoWeek");
      const weekEnd = dayjs(today).subtract(i, "week").endOf("isoWeek");
      const key = `${weekStart.format("DD MMM")} - ${weekEnd.format("DD MMM YYYY")}`;
      labels.push(key);
      earningsByPeriod[key] = 0;
    }
  } else if (timeframe === "monthly") {
    // last 6 months, oldest -> newest, key: YYYY-MMMM
    const months = 6;
    for (let i = months - 1; i >= 0; i--) {
      const m = dayjs(today).subtract(i, "month");
      const key = m.format("YYYY-MMMM");
      labels.push(key);
      earningsByPeriod[key] = 0;
    }
  }

  // --- Fill buckets (use the exact same key logic as above) ---
  expense?.forEach((exp) => {
    const createdAt = dayjs(exp.createdAt);
    let periodKey = "";

    if (timeframe === "custom" || timeframe === "daily") {
      periodKey = createdAt.format("YYYY-MM-DD");
    } else if (timeframe === "weekly") {
      const weekStart = createdAt.startOf("isoWeek").format("DD MMM");
      const weekEnd = createdAt.endOf("isoWeek").format("DD MMM YYYY");
      periodKey = `${weekStart} - ${weekEnd}`;
    } else if (timeframe === "monthly") {
      periodKey = createdAt.format("YYYY-MMMM");
    }

    const value = Number(exp.grandTotal) || 0;
    if (earningsByPeriod[periodKey] !== undefined) {
      earningsByPeriod[periodKey] += value;
    }
  });

  // --- Final labels to display (format daily/custom nicely) ---
  const finalLabels = labels.map((l) =>
    timeframe === "daily" || timeframe === "custom"
      ? dayjs(l).format("MMM DD")
      : l
  );

  // --- Build data array in exact same order as labels (no .reverse()) ---
  const dataArr = labels.map((key) => earningsByPeriod[key] || 0);

  return {
    labels: finalLabels,
    datasets: [
      {
        label:
          timeframe === "daily"
            ? "Daily Earnings"
            : timeframe === "weekly"
            ? "Weekly Earnings"
            : timeframe === "monthly"
            ? "Monthly Earnings"
            : `Earnings (${dayjs(startDate).format("DD MMM")} - ${dayjs(
                endDate
              ).format("DD MMM YYYY")})`,
        data: dataArr,
        backgroundColor: "rgba(54, 162, 235, 0.6)",
        borderColor: "rgba(54, 162, 235, 1)",
        borderWidth: 1,
      },
    ],
  };
};

// Optional: improve doctor breakdown chart (stable order)
const processExpensedoctorData = () => {
  if (!expense) return { labels: [], datasets: [] };

  const earningsByDoctor = {};

  expense.forEach((exp) => {
    const doctorName = exp?.appointment?.doctor?.name || "Unknown";
    earningsByDoctor[doctorName] = (earningsByDoctor[doctorName] || 0) + (Number(exp.grandTotal) || 0);
  });

  // sort doctors by earnings descending (nice visualization)
  const entries = Object.entries(earningsByDoctor).sort((a, b) => b[1] - a[1]);
  const labels = entries.map((e) => e[0]);
  const data = entries.map((e) => e[1]);

  return {
    labels,
    datasets: [
      {
        label: "Doctor's Earnings",
        data,
        backgroundColor: "rgba(75, 192, 192, 0.6)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 1,
        fill : true
      },
    ],
  };
};

    const handlePaidStatusChange = (expenseId, value) => {
        // Dispatch the update action
        dispatch(updateExpense({ _id: expenseId, paid: value }));
        const { startDate, endDate, doctor } = filters;
        const formattedStartDate = startDate
            ? dayjs(startDate).format("YYYY-MM-DD")
            : null;
        const formattedEndDate = endDate
            ? dayjs(endDate).format("YYYY-MM-DD")
            : null;
        dispatch(
            listExpenses(
                pagination.currentPage,
                pagination.pageSize,
                doctor,
                formattedStartDate,
                formattedEndDate
            )
        );
    };
const isCustomRange =
  filters.startDate &&
  filters.endDate &&
  view === "custom"; // only use custom if explicitly chosen

const chartData = isCustomRange
  ? processExpenseData("custom", filters.startDate, filters.endDate)
  : processExpenseData(view);

    return (
        <Layout title="REVENUE" className="container-fluid">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="btn-group mb-4" role="group">
                    <button
                        className={`btn ${
                            view === "daily"
                                ? "btn-primary"
                                : "btn-outline-primary"
                        }`}
                        onClick={() => setView("daily")}
                    >
                        Daily
                    </button>
                    <button
                        className={`btn ${
                            view === "weekly"
                                ? "btn-primary"
                                : "btn-outline-primary"
                        }`}
                        onClick={() => setView("weekly")}
                    >
                        Weekly
                    </button>
                    <button
                        className={`btn ${
                            view === "monthly"
                                ? "btn-primary"
                                : "btn-outline-primary"
                        }`}
                        onClick={() => setView("monthly")}
                    >
                        Monthly
                    </button>
                    {userInfo.role !== 1 && (
                        <button
                            className={`btn ${
                                view === "doctor"
                                    ? "btn-primary"
                                    : "btn-outline-primary"
                            }`}
                            onClick={() => setView("doctor")}
                        >
                            Doctor
                        </button>
                    )}
                </div>

                <div className="d-flex" style={{ gap: "5px" }}>
                    <RangePicker
                        placeholder={["Start Date", "End Date"]}
                        onChange={handleDateChange}
                        allowClear
                        value={filters.startDate && filters.endDate ? [
                            dayjs(filters.startDate),
                            dayjs(filters.endDate).subtract(1, 'day')
                        ] : null}
                    />
                    {userInfo.role !== 1 ? (
                        <Select
                            placeholder="Select Doctor"
                            style={{ width: 200 }}
                            onChange={handleDoctorChange}
                            allowClear
                        >
                            <Option key={0} value={null}>
                                All
                            </Option>
                            {/* Render doctor options dynamically */}
                            {users &&
                                users.length !== 0 &&
                                users
                                    .filter((user) => user.role === 1)
                                    ?.map((doc) => (
                                        <Option key={doc._id} value={doc._id}>
                                            {doc?.name}
                                        </Option>
                                    ))}
                        </Select>
                    ) : (
                        ""
                    )}
                    <Button type="primary" onClick={applyFilters}>
                        Search
                    </Button>
                </div>
            </div>

            <div className="mt-4">
                <h3>
                    {view === "doctor"
                        ? ""
                        : view === "daily"
                        ? "Daily Earnings (Last 7 Days)"
                        : view === "weekly"
                        ? "Weekly Earnings (Last 4 Weeks)"
                        : "Monthly Earnings (Last 6 Months)"}
                </h3>
                <div>
                    {!expense || expense.length === 0 ? (
                        <div className="text-center p-5" style={{ background: "#f8f9fa", borderRadius: "8px", border: "2px dashed #dee2e6" }}>
                            <div style={{ fontSize: "48px", color: "#6c757d", marginBottom: "16px" }}>
                                📊
                            </div>
                            <h4 style={{ color: "#6c757d", marginBottom: "8px" }}>No Data Available</h4>
                            <p style={{ color: "#6c757d", margin: "0" }}>
                                No expenses found.
                            </p>
                        </div>
                    ) : (
view === "doctor" ? (
  <Bar
    data={processExpensedoctorData()}
    options={{
      responsive: true,
      plugins: {
        legend: { display: true, position: 'top' },
      },
      scales: {
        y: {
          beginAtZero: true,
        },
      },
    }}
  />
) : (
  <Line data={chartData} options={{ responsive: true }} />
)

                    )}
                </div>
            </div>

            {loading ? (
                <div className="text-center">
                    <div className="spinner-border" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            ) : error ? (
                <div className="alert alert-danger">{error}</div>
            ) : !expense || expense.length === 0 ? (
                <div className="alert alert-info">No Expenses Found</div>
            ) : (
                <Fragment>
                    {userInfo.role === 0 ? (
                        <div className="mt-4">
                            <h2 className="mb-4">Revenue by Doctor</h2>
                            <table
                                className="table table-bordered "
                                style={{ width: "100%" }}
                            >
                                <thead className="table-dark">
                                    <tr>
                                        <th>#</th>
                                        <th>Doctor Name</th>
                                        <th>Total Revenue</th>
                                        <th>Total Discount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {expensesByDoctor.map((expense, index) => (
                                        <tr key={expense._id}>
                                            <td>{index + 1}</td>

                                            <td>Doctor {expense.doctorName}</td>
                                            <td>₹ {expense.totalRevenue}</td>
                                            <td>₹ {expense.totalDiscount}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        ""
                    )}
                    <div className="table-responsive my-4">
                        <table className="table table-striped table-hover">
                            <thead className="thead-dark">
                                <tr className="text-center">
                                    <th>ID</th>
                                    <th>Doctor</th>
                                    <th>Patient</th>
                                    <th>Date</th>
                                    <th>Grand Total</th>
                                    <th>dueAmount</th>
                                    <th>Paid</th>
                                    <th>Payment Method</th>
                                    <th>Total Cost</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {expense &&
                                    expense.map((exp, i) => (
                                        <tr key={i} className="text-center">
                                            <td>{exp._id.substring(0, 7)}</td>
                                            <td>
                                                {exp.appointment?.doctor?.name}
                                            </td>
                                            <td>
                                                {exp?.appointment?.patient
                                                    ?.firstName +
                                                    " - " +
                                                    exp?.appointment?.patient
                                                        ?.patientNumber}
                                            </td>
                                            <td>
                                                {dayjs(exp.createdAt).format(
                                                    "YYYY-MM-DD"
                                                )}
                                            </td>
                                            <td>₹ {exp.grandTotal}</td>
                                            <td>₹ {exp?.totalDue}</td>
                                            <td>
                                                <Select
                                                    value={
                                                        exp?.totalDue
                                                            ? "Un-paid"
                                                            : "Paid"
                                                    }
                                                    onChange={(value) =>
                                                        handlePaidStatusChange(
                                                            exp._id,
                                                            value
                                                        )
                                                    }
                                                    style={{ width: 120 }}
                                                >
                                                    <Option value="Paid">
                                                        Paid
                                                    </Option>
                                                    <Option value="Un-paid">
                                                        Un-paid
                                                    </Option>
                                                </Select>
                                            </td>

                                            <td>{exp.paymentMethod}</td>
                                            <td>₹ {exp.totalCost}</td>
                                            <td>
                                                <div
                                                    className="btn-group"
                                                    role="group"
                                                >
                                                    {/* <Button
													type="primary"
													size="small"
													onClick={() =>
														navigate(
															`/update-expenses/${exp._id}`
														)
													}>
													Edit
												</Button> */}
                                                    <Button
                                                        type="danger"
                                                        size="small"
                                                        onClick={() =>
                                                            deleteHandler(
                                                                exp._id
                                                            )
                                                        }
                                                    >
                                                        Delete
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="my-4">
                        <Pagination
                            current={pagination.currentPage}
                            total={totalExpenses}
                            pageSize={pagination.pageSize}
                            onChange={handlePaginationChange}
                            showSizeChanger
                            pageSizeOptions={["10", "20", "50"]}
                            onShowSizeChange={handlePaginationChange}
                        />
                    </div>
                </Fragment>
            )}
        </Layout>
    );
};

export default ListExpenses;